import { neftApi } from "app/config";

import { ApiResponse, TransactionDTO, ApiTableResponse, TransactionParams } from "models";
import { removeEmptyValue } from "utils";

const transactionApiWithTag = neftApi.enhanceEndpoints({
  addTagTypes: [
    "transaction",
    "batch-summary",
    "sortcode-enum",
    "transaction-push",
    "transaction-pull",
    "transaction-batch",
    "transaction-error",
    "nibss-status-enum",
    "transaction-type-enum",
  ],
});

const transactionApiSlice = transactionApiWithTag.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    findTransactionById: builder.query<ApiResponse<TransactionDTO>, { id: string }>({
      query: ({ id }) => ({
        url: `/transaction/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, tags) => [{ type: "transaction" as const, id: tags.id }],
    }),

    getTransactions: builder.query<ApiTableResponse<TransactionDTO>, TransactionParams>({
      query: (transactionParams) => ({
        url: `/transaction/search`,
        method: "GET",
        params: removeEmptyValue(transactionParams),
      }),
      providesTags: (result, error, tags) => [{ type: "transaction-batch" }],
    }),

    getTransactionsWithError: builder.query<ApiTableResponse<TransactionDTO>, TransactionParams>({
      query: ({ batchId, ...params }) => ({
        url: `/transaction/error/${batchId}`,
        method: "GET",
        params: removeEmptyValue(params),
      }),
      providesTags: (result, error, tags) => [
        { type: "transaction-error" as const, id: tags.batchId },
      ],
    }),

    refreshTRXNStatus: builder.mutation<ApiResponse<string>, void>({
      query: () => ({
        url: `/pull`,
        method: "PUT",
      }),
      invalidatesTags: [{ type: "audit" }, { type: "batch-summary" }] as any,
    }),

    pushAllBatchToNIBSS: builder.mutation<ApiResponse<string>, void>({
      query: () => ({
        url: `/push`,
        method: "PUT",
      }),
      invalidatesTags: [
        { type: "audit" },
        { type: "batch-summary" },
        { type: "transaction-batch" },
      ] as any,
    }),

    updateApprover: builder.mutation<ApiResponse<string>, { batchIds: string[] }>({
      query: ({ batchIds, ...rest }) => ({
        url: `/approvals/updateApprover/batches`,

        method: "PATCH",
        body: batchIds,
        params: removeEmptyValue(rest),
      }),
      invalidatesTags: [
        { type: "audit" },
        { type: "batch-summary" },
        { type: "transaction-batch" },
      ] as any,
    }),

    approveBatches: builder.mutation<ApiResponse<string>, { batchIds: string[] }>({
      query: ({ batchIds, ...rest }) => ({
        url: `/approvals/decision/batches`,

        method: "PATCH",
        body: batchIds,
        params: removeEmptyValue(rest),
      }),
      invalidatesTags: [
        { type: "audit" },
        { type: "batch-summary" },
        { type: "transaction-batch" },
      ] as any,
    }),

    pushSingleBatchToNIBSS: builder.mutation<ApiResponse<string>, { batchId: string }>({
      query: ({ batchId }) => ({
        url: `/push/${batchId}`,
        method: "PUT",
      }),
      invalidatesTags: [
        { type: "audit" },
        { type: "batch-summary" },
        { type: "transaction-batch" },
      ] as any,
    }),

    refreshSingleBatchStatus: builder.mutation<ApiResponse<string>, { batchId: string }>({
      query: ({ batchId }) => ({
        url: `/pull/${batchId}`,
        method: "PUT",
      }),
      invalidatesTags: [
        { type: "audit" },
        { type: "batch-summary" },
        { type: "transaction-batch" },
      ] as any,
    }),
  }),
});

export const {
  useGetTransactionsQuery,
  useApproveBatchesMutation,
  useUpdateApproverMutation,
  useFindTransactionByIdQuery,
  useRefreshTRXNStatusMutation,
  usePushAllBatchToNIBSSMutation,
  useGetTransactionsWithErrorQuery,
  usePushSingleBatchToNIBSSMutation,
  useRefreshSingleBatchStatusMutation,
} = transactionApiSlice;

export default transactionApiSlice;
